import React, { useEffect, useState } from 'react';
import { useAuth } from 'providers/AuthProvider';
import GetAuthToken from 'utils/getAccessToken';

import createApiClient from 'utils/axios';
import ResponseDecryption from 'utils/responseDecryption';
import RequestEncryption from 'utils/requestEncryption';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode } from "@fortawesome/free-solid-svg-icons";

//Utils
import { copyToClipboard } from "utils/commonUtility";

export default function TryItOut({ endpoint, method, requestSchema, requiredFields, payloadEncryption, responseDecryption = false}) {
    const [requestData, setRequestData] = useState({});
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [curlCommand, setCurlCommand] = useState('');
    const { authToken, updateAuth } = useAuth(); // Add this line to get authToken
    const [encRequest, setEncRequest] = useState("");
    const [updatedEndpoint, setUpdatedEndpoint] = useState(null);
    const [updatedEndpointFlag, setUpdatedEndpointFlag] = useState(false);
    const [copied, setCopied] = useState(false);
    
    const apiUrl = process.env.REACT_APP_UAT_AUTH_URL;
    const secretKey = process.env.REACT_APP_SECRET_KEY;
    const clientId = process.env.REACT_APP_CLIENT_ID;
    const xapikey = process.env.REACT_APP_UAT_X_API_KEY;
    const baseUrl = process.env.REACT_APP_UAT_BASE_URL;

    useEffect(() => {
        setResponse(null);
        setResponse("");
        setError(null);
        setRequestData({});
        setUpdatedEndpoint(null);
        setUpdatedEndpointFlag(false);
        setEncRequest("");
        setCurlCommand(generateCurlCommand(method, endpoint, {}, authToken));
    }, [endpoint]);

    // Add this helper function after the imports
    const generateCurlCommand = (method, endpoint, data, authToken) => {
        let curlParts = [];

        // Base curl command
        curlParts.push(`<span class="text-yellow-500 mr-2">curl</span><span class="text-green-400 mr-2">-X</span><span class="text-blue-400">'${method}'</span><span class="text-green-400 mr-2">--location</span><span class="text-blue-400">'${baseUrl}${endpoint}'</span>`);

        // Headers
        curlParts.push(`<span class="text-green-400 mr-2">--header</span><span class="text-blue-400">'Content-Type: application/json'</span>`);
        // Headers
        curlParts.push(`<span class="text-green-400 mr-2">--header</span><span class="text-blue-400">'x-api-key: ${xapikey}'</span>`);

        if (authToken) {
            curlParts.push(`<span class="text-green-400 mr-2">--header</span><span class="text-blue-400">'Authorization: Bearer ${authToken}'</span>`);
        }

        // Request body
        if (payloadEncryption == false && method !== 'get' && data && Object.keys(data).length > 0) {
            curlParts.push(`<span class="text-green-400">--data</span>`);
            curlParts.push(`<span class="text-blue-400">'${JSON.stringify(data, null, 2)}'</span>`);
        } 
        else if(Object.keys(data).length !== 0) {
            curlParts.push(`<span class="text-green-400">--data</span>`);
            curlParts.push(`<span class="text-blue-400">'${JSON.stringify(data, null, 2)}'</span>`);
        }

        return curlParts.join(' \\\n');
    };

    const handleInputChange = (key, value, type = 'string') => {
        let parsedValue = value;
        if (type === 'number') parsedValue = Number(value);
        if (type === 'boolean') parsedValue = value === 'true';
        if (type === 'array') parsedValue = value.split(",");
        
        setRequestData(prev => ({
            ...prev,
            [key]: parsedValue
        }));
    };

    const handleBlurEvent = (key, value, type = 'string') => {
        let parsedValue = value;
        if (type === 'number') parsedValue = Number(value);
        if (type === 'boolean') parsedValue = value === 'true';
        if (type === 'array') parsedValue = value.split(",");

        setRequestData(prev => ({
            ...prev,
            [key]: parsedValue
        }));
        
        if(endpoint.includes(`{${key}}`)){
            endpoint = endpoint.replace(`{${key}}`, parsedValue);

            setUpdatedEndpoint(endpoint);
            setUpdatedEndpointFlag(true);

            setCurlCommand(generateCurlCommand(method, endpoint, {}, authToken));
        }else{
            setUpdatedEndpoint(null);
            setUpdatedEndpointFlag(false);

            // Update curl command
            setCurlCommand(generateCurlCommand(method, endpoint, requestData, authToken));
        }
    }

    const renderInputField = (key, schema) => {
        const type = schema.type;
        const required = schema.required;

        switch (type) {
            case 'string':
                return (
                    <input
                        type="text"
                        className="w-full p-2 border rounded"
                        onChange={(e) => handleInputChange(key, e.target.value)}
                        onBlur={(e) => handleBlurEvent(key, e.target.value)}
                        required={requiredFields.includes(key) ? true : false}
                        placeholder={schema.description || key}
                    />
                );
            case 'array':
                return (
                    <input
                        type="text"
                        className="w-full p-2 border rounded"
                        onChange={(e) => handleInputChange(key, e.target.value, type)}
                        onBlur={(e) => handleBlurEvent(key, e.target.value, type)}
                        required={requiredFields.includes(key) ? true : false}
                        placeholder="value1, value2, value3"
                    />
                );
            case 'number':
                return (
                    <input
                        type="number"
                        className="w-full p-2 border rounded"
                        onChange={(e) => handleInputChange(key, e.target.value, 'number')}
                        onBlur={(e) => handleBlurEvent(key, e.target.value, 'number')}
                        required={requiredFields.includes(key) ? true : false}
                        placeholder={schema.description || key}
                    />
                );
            case 'boolean':
                return (
                    <select
                        className="w-full p-2 border rounded"
                        onChange={(e) => handleInputChange(key, e.target.value, 'boolean')}
                        onBlur={(e) => handleBlurEvent(key, e.target.value, 'boolean')}
                        required={requiredFields.includes(key) ? true : false}
                    >
                        <option value="">Select</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                    </select>
                );
            default:
                return null;
        }
    };

    const handleTryIt = async () => {
        setLoading(true);
        setError(null);
        var encryptedData = {};
        let encRequest = {};
        
        if(payloadEncryption){
            // Encrypt request
            encryptedData = await RequestEncryption(JSON.stringify(requestData), authToken);
            console.log('Encrypted data:', encryptedData);
            encRequest = {"encryptedData" : encryptedData};
            
            setRequestData(encRequest);
            setCurlCommand(generateCurlCommand(method, endpoint, encRequest, authToken));
        }else{
            setRequestData({});
        }

        var jsonData = payloadEncryption ? encRequest : requestData;

        try {
            // If we already have a token, just make the request
            let configuredClient = createApiClient(authToken);

            if (!authToken) {
                setError("Auth Token is Expired. Please re-authenticate.");
                setLoading(false);
                return;
            }
            
            const response = await configuredClient({
                method: method.toLowerCase(),
                url: updatedEndpointFlag ? updatedEndpoint : endpoint,
                data: method !== 'get' ? JSON.stringify(jsonData) : null,
                params: method === 'get' ? JSON.stringify(jsonData) : null,
            });
            console.log('API response:', response);
            
            setLoading(false);
            if(responseDecryption){
                // Decrypt response
                const decryptedResponse = await ResponseDecryption(response.data, authToken);
                console.log('Decrypted data:', decryptedResponse);

                setResponse(decryptedResponse);
            }else{
                setResponse(response.data);
            }
        } catch (err) {
            setError(err.response?.data || err.message);
        } finally {
            setLoading(false);
        }
    }

    const handleCopy = () => {
        const codeElement = document.getElementById("curlCommand");
        const text = codeElement.innerText || codeElement.textContent;

        copyToClipboard(text, setCopied);
    }

    return (
        <div className="bg-white rounded-lg shadow p-6 pt-0">
            <div className="mb-6">
                <div class="bg-blue-400 text-white text-center p-2 rounded-lg shadow-lg max-w-2xl mx-auto">
                    <p class="text-2xl font-semibold justify-between flex items-center">
                        <span className='ml-2'>Try It Yourself</span>
                        <FontAwesomeIcon className='ml-2 mr-3' icon={faCode} />
                    </p>
                </div>
                <h3 className="text-lg font-semibold mb-4 mt-5">Request Parameters</h3>
                <div className="space-y-4">
                    {requestSchema && Object.entries(requestSchema.properties || {}).map(([key, schema]) => (
                        <div key={key} className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {key} {requiredFields.includes(key) && <span className="text-red-500 ml-2">Required</span>}
                            </label>
                            {renderInputField(key, schema)}
                            {/* {schema.description && (
                                <p className="text-xs text-gray-500">{schema.description}</p>
                            )} */}
                        </div>
                    ))}
                </div>
            </div>

            <div className='text-end'>
                <button
                    onClick={handleTryIt}
                    disabled={loading}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
                >
                    {loading ? 'Loading...' : 'Run'}
                </button>
            </div>
            
            {/* Add this section before the button */}
            {curlCommand && (
                <div className="mt-4 mb-6">
                    <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-white font-semibold">REQUEST</span>
                            <button
                                onClick={() => handleCopy()}
                                className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-sm"
                            >
                                {!copied ? <span>Copy</span> : <span className="text-green-500">Copied!</span>}
                            </button>
                        </div>
                        <div className="bg-gray-700 rounded-md p-3 text-sm text-gray-300 relative">
                            <pre id='curlCommand' className="overflow-x-auto whitespace-pre">
                                <code dangerouslySetInnerHTML={{ __html: curlCommand }} />
                            </pre>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="mt-4">
                    <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-white font-semibold">RESPONSE</span>
                            <div className="flex gap-2">
                                <span className="px-2 py-1 rounded bg-red-500 text-white text-xs">
                                    {'FAILED'}
                                </span>
                                <button
                                    onClick={() => navigator.clipboard.writeText(JSON.stringify(response, null, 2))}
                                    className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-sm"
                                >
                                    Copy
                                </button>
                            </div>
                        </div>
                        <div className="bg-gray-700 rounded-md p-3 text-sm relative">
                            <pre className="overflow-x-auto whitespace-pre text-gray-300">
                                <code>
                                    {JSON.stringify(error, null, 2)}
                                </code>
                            </pre>
                        </div>
                    </div>
                </div>
            )}

            {response && (
                <div className="mt-4">
                    <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-white font-semibold">RESPONSE</span>
                            <div className="flex gap-2">
                                <span className="px-2 py-1 rounded bg-green-500 text-white text-xs">
                                    {response.status || 'SUCCESS'}
                                </span>
                                <button
                                    onClick={() => 
                                        navigator.clipboard.writeText(JSON.stringify(response, null, 2))
                                    }
                                    className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-sm"
                                >
                                    Copy
                                </button>
                            </div>
                        </div>
                        <div className="bg-gray-700 rounded-md p-3 text-sm relative">
                            <pre className="overflow-x-auto whitespace-pre text-gray-300">
                                <code>
                                    {JSON.stringify(response, null, 2)}
                                </code>
                            </pre>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}